import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { Bold, Italic, Heading2, List, ListOrdered, Quote } from 'lucide-react';

/**
 * WYSIWYG editor whose value is markdown. The operator sees real formatting
 * while typing (bold, lists, headings, quotes); the stored string stays plain
 * markdown so it round-trips cleanly into the RAG index and matches what the
 * chat <Markdown> renderer produces.
 */
function ToolbarButton({ active, onClick, label, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={clsx(
        'inline-flex h-7 w-7 items-center justify-center rounded transition-colors',
        active ? 'bg-ink text-white' : 'text-ink-muted hover:bg-surface-subtle hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}

// Prose styling for the editable area, mirroring the chat Markdown renderer.
const PROSE = [
  '[&_.ProseMirror]:outline-none',
  '[&_.ProseMirror>*:first-child]:mt-0',
  '[&_p]:my-1.5 [&_p]:leading-relaxed',
  '[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:marker:text-ink-faint',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_h1]:mb-1 [&_h1]:mt-2 [&_h1]:text-base [&_h1]:font-semibold',
  '[&_h2]:mb-1 [&_h2]:mt-2 [&_h2]:text-sm [&_h2]:font-semibold',
  '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border-strong [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-ink-muted',
  '[&_code]:rounded [&_code]:bg-border/40 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
  '[&_a]:text-brand [&_a]:underline'
].join(' ');

export function MarkdownEditor({ value = '', onChange, minHeight = 260, placeholder }) {
  const lastValue = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({ html: false, transformPastedText: true, transformCopiedText: true })
    ],
    content: '',
    editorProps: { attributes: { class: 'focus:outline-none', 'aria-label': placeholder || 'Editor' } },
    onUpdate: ({ editor: ed }) => {
      const md = ed.storage.markdown.getMarkdown();
      lastValue.current = md;
      onChange(md);
    }
  });

  // Sync external value changes (initial seed, switching item) without fighting
  // the cursor: compare against the last value WE synced, not the editor's
  // re-serialised markdown (which may differ by harmless normalisation).
  useEffect(() => {
    if (!editor) return;
    if (value === lastValue.current) return;
    lastValue.current = value;
    editor.commands.setContent(value || '', false);
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-md border border-border bg-surface-elevated">
      <div className="flex items-center gap-0.5 border-b border-border px-2 py-1.5">
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Grassetto"><Bold className="h-3.5 w-3.5" /></ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Corsivo"><Italic className="h-3.5 w-3.5" /></ToolbarButton>
        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Titolo"><Heading2 className="h-3.5 w-3.5" /></ToolbarButton>
        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Elenco puntato"><List className="h-3.5 w-3.5" /></ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Elenco numerato"><ListOrdered className="h-3.5 w-3.5" /></ToolbarButton>
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Citazione"><Quote className="h-3.5 w-3.5" /></ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        style={{ '--md-min-h': `${minHeight}px` }}
        className={clsx('max-h-[55vh] overflow-y-auto px-3 py-2 text-sm text-ink [&_.ProseMirror]:min-h-[var(--md-min-h)]', PROSE)}
      />
    </div>
  );
}
